/**
 * Statistics Service
 *
 * Loads age-specific Canadian financial statistics from static JSON files
 * served under /data/statistics/age-{age}.json. Fetched on demand and
 * cached in sessionStorage so subsequent loads for the same age are instant.
 */

import type { AgeStatistics, AgeStatisticsFile } from "../types/statistics";
import { formatStatisticHelpText } from "../types/statistics";
import { createLogger } from "../utils/logger";

const logger = createLogger({ prefix: "StatisticsService" });

/** In-memory cache: avoids JSON.parse on every access within the same session. */
let cachedMap: AgeStatistics | null = null;
let cachedAge: number | null = null;

/**
 * Clamp and round an age to the 18–100 range covered by the static files.
 */
function normalizeAge(age: number): number {
  return Math.max(18, Math.min(100, Math.round(age)));
}

/**
 * Load per-age statistics. Returns a flat map of fieldName → AgeFieldStatistic.
 *
 * Resolution order:
 *   1. In-memory cache (same page lifetime)
 *   2. sessionStorage (survives soft-navigations)
 *   3. HTTP fetch from /data/statistics/age-{age}.json
 */
export async function loadStatisticsForAge(
  age: number
): Promise<AgeStatistics> {
  const normAge = normalizeAge(age);

  // 1. Memory cache
  if (cachedMap && cachedAge === normAge) return cachedMap;

  // 2. sessionStorage
  const storageKey = `statistics_age_${normAge}`;
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as AgeStatisticsFile;
      cachedMap = parsed.fields;
      cachedAge = normAge;
      return cachedMap;
    }
  } catch {
    // sessionStorage may be unavailable (SSR, quota exceeded, etc.)
  }

  // 3. Fetch
  const url = `/data/statistics/age-${normAge}.json`;
  const response = await fetch(url);
  if (!response.ok) {
    logger.error("Failed to load statistics file", {
      url,
      status: response.status,
    });
    throw new Error(
      `Failed to load statistics for age ${normAge} (HTTP ${response.status})`
    );
  }

  const file: AgeStatisticsFile = await response.json();

  cachedMap = file.fields;
  cachedAge = normAge;

  // Persist to sessionStorage for subsequent soft-navigations
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(file));
  } catch {
    // Quota exceeded — non-critical
  }

  logger.info(`Loaded statistics for age ${normAge}`, {
    fieldCount: Object.keys(cachedMap).length,
  });

  return cachedMap;
}

/**
 * Synchronous lookup of the age-appropriate average for a field.
 *
 * The caller must supply a pre-loaded StatisticsMap (typically loaded once
 * in SettingsContext via {@link loadStatisticsForAge}).
 *
 * @returns The average value for the field, or 0 if unavailable.
 */
export function getFieldStatisticValue(
  statisticsMap: AgeStatistics,
  fieldName: string
): number {
  const stat = statisticsMap[fieldName];
  if (!stat) {
    throw new Error(
      `Missing statistic for field "${fieldName}". Statistics must be loaded before accessing field values.`
    );
  }
  return stat.average;
}

/**
 * Format help text for a field from a pre-loaded StatisticsMap.
 *
 * @returns e.g. "(Age average: ~$52,000)" or "" if the field has no statistic.
 */
export function getFieldHelpText(
  statisticsMap: AgeStatistics,
  fieldName: string
): string {
  const stat = statisticsMap[fieldName];
  if (!stat) return "";
  return formatStatisticHelpText(stat);
}

/**
 * Reset the in-memory cache (useful for tests).
 */
export function _resetStatisticsCache(): void {
  cachedMap = null;
  cachedAge = null;
}
